'use client'

import Link from 'next/link'
import type { CSSProperties } from 'react'

export interface NewsCategoryTagItem {
  id: number
  tag: {
    id: number
    tag: string
    slug: string
  }
}

interface NewsCategoryTagProps {
  tags?: NewsCategoryTagItem[]
  category: {
    id: number
    title: string
    slug: string
    color?: string | null
  } | null
  className?: string
  variant?: 'default' | 'rounded-full'
  /** Açık kart, koyu overlay veya sade siyah-beyaz */
  surface?: 'light' | 'dark' | 'mono'
  /** false when rendered inside another link (avoids nested <a>) */
  linkable?: boolean
  /** @deprecated Kategoriler konu etiketi değildir; geriye uyumluluk için tutulur. */
  categoryAsHashtag?: boolean
}

const DEFAULT_COLOR = '#D92E2E'

const MONO_CHIP_CLASS =
  'news-category-tag news-category-tag--mono inline-flex max-w-full items-center truncate rounded border-0 bg-black/75 px-2 py-0.5 text-[10px] font-medium text-white transition-colors hover:bg-black/90'

function parseHexColor(input?: string | null): { r: number; g: number; b: number } {
  const raw = (input || DEFAULT_COLOR).trim().replace('#', '')
  if (raw.length === 3) {
    return {
      r: parseInt(raw[0] + raw[0], 16),
      g: parseInt(raw[1] + raw[1], 16),
      b: parseInt(raw[2] + raw[2], 16),
    }
  }
  if (raw.length === 6) {
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16),
    }
  }
  return { r: 217, g: 46, b: 46 }
}

function getCategoryTintStyle(color: string | null | undefined): CSSProperties {
  const { r, g, b } = parseHexColor(color)
  const luminance =
    (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255

  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.8)`,
    borderColor: `rgba(${r}, ${g}, ${b}, 0.92)`,
    color: luminance > 0.62 ? '#111827' : '#ffffff',
  }
}

function normalizeComparableLabel(value?: string | null): string {
  return (value || '')
    .trim()
    .toLocaleLowerCase('tr-TR')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9çğıöşü]/g, '')
}

function getChipClass(surface: 'light' | 'dark', variant: 'default' | 'rounded-full') {
  const radius = variant === 'rounded-full' ? 'rounded-full' : 'rounded-[4px]'
  const size =
    variant === 'rounded-full'
      ? 'px-2.5 py-1 text-[11px] sm:px-3 sm:text-xs'
      : 'px-1.5 py-0.5 text-[10px]'

  const tone =
    surface === 'dark'
      ? 'font-semibold backdrop-blur-[2px] shadow-[0_1px_2px_rgba(0,0,0,0.28)]'
      : 'font-bold tracking-[0.02em]'

  return ['news-category-tag', 'inline-flex max-w-full items-center truncate border', radius, size, tone].join(' ')
}

/**
 * Etiket varsa ilk etiketi, yoksa kategoriyi gösterir.
 */
export default function NewsCategoryTag({
  tags,
  category,
  className = '',
  variant = 'default',
  surface = 'dark',
  linkable = true,
}: NewsCategoryTagProps) {
  const firstTag = tags?.[0]?.tag
  const categorySlug = category?.slug || 'haber'
  const categoryTitle = category?.title || 'Haber'
  const tagMatchesCategory = Boolean(
    firstTag?.tag &&
      firstTag?.slug &&
      (normalizeComparableLabel(firstTag.slug) === normalizeComparableLabel(categorySlug) ||
        normalizeComparableLabel(firstTag.tag) === normalizeComparableLabel(categoryTitle)),
  )
  const isTagLabel = Boolean(firstTag?.tag && firstTag?.slug && !tagMatchesCategory)
  const label = isTagLabel
    ? `#${firstTag!.tag}`
    : categoryTitle
  const href = isTagLabel ? `/haberleri/${firstTag!.slug}` : `/${categorySlug}`

  const chipClass =
    surface === 'mono'
      ? `${MONO_CHIP_CLASS} ${className}`.trim()
      : `${getChipClass(surface, variant)} ${className}`.trim()
  const tintStyle = surface === 'mono' ? undefined : getCategoryTintStyle(category?.color)
  const interactiveClass =
    linkable && surface !== 'mono' ? 'transition-colors hover:brightness-110' : ''

  if (!linkable) {
    return (
      <span className={chipClass} style={tintStyle}>
        {label}
      </span>
    )
  }

  return (
    <Link
      href={href}
      className={`${chipClass} ${interactiveClass}`.trim()}
      style={tintStyle}
      onClick={(event) => event.stopPropagation()}
    >
      {label}
    </Link>
  )
}
